import React, { useState, useCallback, useMemo, useEffect } from "react"
import { Form, Row, Col, Button } from "react-bootstrap"
import { useNavigate } from "react-router-dom"
import Layout from "../components/layout/Layout"
import useAuthAxios from "../hooks/useAuthAxios"
import { useAuth } from "../context/AuthContext"
import { filterEmptyFields } from "../utils/searchUtils"
import { usePersistedState } from "../hooks/usePersistedState"
import { toast } from "react-toastify"
import DataTable from "../components/common/DataTable"
import { formatDateForDisplay } from "../utils/dateUtils"

const VehiclesSearch = () => {
  const navigate = useNavigate()
  const { get, loading, error } = useAuthAxios()
  const { appLookups } = useAuth()

  // Use persisted state for search results and criteria
  const [vehicles, setVehicles] = usePersistedState("vehicles", [])
  const [hasSearched, setHasSearched] = useState(false)
  const [searchCriteria, setSearchCriteria] = usePersistedState("vehicleSearchCriteria", {
    InsComp: "",
    ClaimNo: ""
  })

  const companyOptions = useMemo(() => {
    if (!appLookups) return []
    return appLookups
      .filter(lookup => lookup.Table === "Companies")
      .sort((a, b) => a.Name.localeCompare(b.Name))
  }, [appLookups])

  useEffect(() => {
    companyOptions?.forEach(company => {
      if (company.Name === "APEIRON") {
        setSearchCriteria(prev => ({
          ...prev,
          InsComp: company.Code
        }))
      }
    })
  }, [companyOptions, setSearchCriteria])

  // Handle search form submission
  const handleSearch = useCallback(
    async e => {
      if (e) {
        e.preventDefault()
      }

      try {
        setHasSearched(true)
        const filteredCriteria = filterEmptyFields(searchCriteria)
        const response = await get("/vehicles", { params: filteredCriteria })
        const items = response.data || []
        setVehicles(items)
      } catch (err) {
        console.error("Failed to fetch vehicles:", err)
        if (err.response?.status !== 404) {
          // Don't show toast for no results
          toast.error("Αποτυχία φόρτωσης οχημάτων. Παρακαλώ, προσπαθήστε ξανά.")
        }
        setVehicles([])
      }
    },
    [get, searchCriteria, setVehicles]
  )

  const handleCreateRepair = useCallback(
    vehicle => {
      navigate("/repairs/new", {
        state: {
          ClaimNo: vehicle.ClaimNo,
          InsuranceCompany: vehicle.InsuranceCompany,
          PlateNumber: vehicle.PlateNumber
        }
      })
    },
    [navigate]
  )

  // Handle reset
  const handleReset = useCallback(() => {
    setSearchCriteria({
      InsComp: "",
      ClaimNo: ""
    })
    setVehicles([])
    setHasSearched(false)
  }, [setSearchCriteria, setVehicles])

  // Handle search criteria changes
  const handleCriteriaChange = (field, value) => {
    setSearchCriteria(prev => ({
      ...prev,
      [field]: value
    }))
  }

  return (
    <Layout>
      <Form onSubmit={handleSearch}>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">Αναζήτηση Οχημάτων</h5>
        </div>
        <Row className="g-3">
          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Ασφαλιστική Εταιρεία</Form.Label>
              <Form.Select
                value={searchCriteria.InsComp}
                onChange={e => handleCriteriaChange("InsComp", e.target.value)}
                disabled={loading}
                required>
                <option value="">Επιλέξτε</option>
                {companyOptions.map(company => (
                  <option key={company.Code} value={company.Code}>
                    {company.Name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col lg={3} md={6} xs={12}>
            <Form.Group className="h-100">
              <Form.Label className="d-block text-nowrap mb-1">Αριθμός Φακέλου</Form.Label>
              <Form.Control
                type="text"
                value={searchCriteria.ClaimNo}
                onChange={e => handleCriteriaChange("ClaimNo", e.target.value)}
                disabled={loading}
                required
              />
            </Form.Group>
          </Col>

          <Col xs={12} className="mt-4">
            <div className="d-flex justify-content-between">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Αναζήτηση...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2" />
                    Αναζήτηση
                  </>
                )}
              </Button>
              <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={loading}>
                <i className="fas fa-undo me-2" />
                Επαναφορά
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      <hr className="my-4" />

      {/* Results Section */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Φόρτωση...</span>
          </div>
        </div>
      ) : error ? (
        <div className="text-center py-5 text-danger">
          <i className="fas fa-exclamation-circle fa-2x mb-3" />
          <p className="mb-0">Παρουσιάστηκε σφάλμα κατά την αναζήτηση.</p>
        </div>
      ) : vehicles.length > 0 ? (
        <DataTable
          data={vehicles}
          loading={loading}
          error={error}
          onRowClick={handleCreateRepair}
          appLookups={appLookups}
          columns={[
            {
              header: "Αριθμός Φακέλου",
              field: "ClaimNo",
              type: "string"
            },
            {
              header: "Αρ. Κυκλοφορίας",
              field: "PlateNumber",
              type: "string"
            },
            {
              header: "Ασφαλιστική",
              field: "InsuranceCompanyName",
              type: "string",
              lookup: "Companies"
            },
            {
              header: "Ημερομηνία Ατυχήματος",
              field: "AccidentDate",
              type: "date",
              render: item => formatDateForDisplay(item.AccidentDate)
            },
            {
              header: "Ασφαλισμένος",
              field: "IsAsfal",
              type: "boolean",
              render: item => (item.IsAsfal ? "Ναι" : "Όχι")
            }
          ]}
        />
      ) : hasSearched ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-search fa-2x mb-3" />
          <p className="mb-0">Δεν βρέθηκαν αποτελέσματα που να αντιστοιχούν στα κριτήρια αναζήτησης.</p>
        </div>
      ) : null}
    </Layout>
  )
}

export default VehiclesSearch
